"use client"

import { useDictionary } from "@/core/hooks/use-dictionary";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput } from "../GlobalWidgetEditor";
import TextInput from "../../input/TextInput";
import { Heading } from "@/components/ui/Heading";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import FileUploadInput from "../../input/MultipleFileUploadInput";



export interface SliderItem {
    image?: any;
    title?: string;
    link?: string;
    title_en?: string;
    title_ar?: string;
    description?: string;
    description_en?: string;
    description_ar?: string;
}


interface SliderWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const SliderWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: SliderWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    const getError = (path: string) => {
        const errorPath = path.split('.');
        let current = errors;
        for (const key of errorPath) {
            if (current && typeof current === 'object' && key in current) {
                current = (current as any)[key];
            } else {
                return undefined;
            }
        }
        return (current as any)?.message;
    };

    const addItem = () => {
        append({
            image: undefined,
            title: '',
            title_en: '',
            title_ar: '',
            description: '',
            description_en: '',
            description_ar: '',
        });
    };

    const removeItem = (sliderIndex: number) => {
        if (fields.length <= 1) return;
        remove(sliderIndex);
    };

    return (
        <>
            {/* بخش جدید - اطلاعات کلی اسلایدر */}
            <div className="p-4 -mx-8 -mt-4 mb-0 rounded-lg bg-primary-100 space-y-5 col-span-2">
                <MultilingualInput
                    baseName={`${name}.${widgetIndex}.title`}
                    label={dictionary.forms?.title || 'عنوان اصلی اسلایدر'}
                    register={register}
                    errors={errors}
                    disabled={disabled}
                />

                <MultilingualTextareaInput
                    baseName={`${name}.${widgetIndex}.description`}
                    label={dictionary.forms?.description || 'توضیحات اصلی اسلایدر'}
                    register={register}
                    errors={errors}
                    disabled={disabled}
                />
                <TextInput
                    {...register(`${name}.${widgetIndex}.url`)}
                    label={dictionary.forms?.link}
                    placeholder={dictionary.forms?.link}
                    register={register}
                    errors={errors}
                    disabled={disabled}
                />
            </div>

            {/* آیتم‌های اسلایدر */}
            {fields.map((field, sliderIndex) => {
                const imageError = getError(
                    `${name}.${widgetIndex}.items.${sliderIndex}.image`
                ) as string | undefined;

                return (
                    <div key={field.id} className="border col-span-2 md:col-span-1 p-4 rounded-md space-y-4 bg-background">
                        <div className="flex justify-between items-center">
                            <Heading level={4}>
                                {dictionary.ui?.form?.sliderItem || 'اسلاید'} {sliderIndex + 1}
                            </Heading>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(sliderIndex)}
                                disabled={fields.length <= 1 || disabled}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>

                        <Controller
                            control={control}
                            name={`${name}.${widgetIndex}.items.${sliderIndex}.image`}
                            render={({ field }) => (
                                <FileUploadInput
                                    accept="image/*"
                                    disabled={disabled}
                                    onUploadSuccess={(image) => {
                                        if (!Array.isArray(image) && image) field.onChange(image);
                                    }}
                                    defaultImages={
                                        field.value
                                            ? [
                                                {
                                                    ...field.value,
                                                    name: field.value.name || '',
                                                    description: field.value.description || '',
                                                },
                                            ]
                                            : []
                                    }
                                    onDefaultRemove={() => field.onChange(undefined)}
                                    error={imageError}
                                />
                            )}
                        />

                        <MultilingualInput
                            baseName={`${name}.${widgetIndex}.items.${sliderIndex}.title`}
                            label={dictionary.forms?.title}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <MultilingualTextareaInput
                            baseName={`${name}.${widgetIndex}.items.${sliderIndex}.description`}
                            label={dictionary.forms?.description}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />
                        <TextInput
                            name={`${name}.${widgetIndex}.items.${sliderIndex}.link`}
                            label={dictionary.forms?.link}
                            placeholder={dictionary.forms?.link}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />
                    </div>
                );
            })}

            <Button
                type="button"
                variant="outline"
                onClick={addItem}
                disabled={disabled}
                className="mt-2"
            >
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " " + dictionary.forms.item}
            </Button>
        </>
    );
};